import * as zod from 'zod';

export const ServiceStatus = zod.object({
  name: zod.string(),
  status: zod.enum(['online', 'offline', 'crash-loop']),
  uptime: zod.string().optional(),
  containerName: zod.string().optional(),
  imageTag: zod.string().optional(),
  startedAt: zod.string().optional(),
});

export const DockerServicesDiagnosticsData = zod.object({
  services: zod.array(ServiceStatus),
});

export const EncoderErrors = zod.object({
  inputEncoder1: zod.boolean(),
  inputEncoder2: zod.boolean(),
  outputEncoder1: zod.boolean(),
  outputEncoder2: zod.boolean(),
  lastErrorTime: zod.number().nullable(),
});

export const JointStatus = zod.object({
  jointNumber: zod.number(),
  status: zod.enum(['connected', 'disconnected', 'warning']),
  lastMessageTime: zod.number().nullable(),
  // Encoder errors (only for joints 0-5)
  encoderErrors: EncoderErrors.nullable(),
});

export const JointDiagnosticsData = zod.object({
  joints: zod.array(JointStatus),
});

export const CANMessageStatus = zod.object({
  messageType: zod.number(),
  jointID: zod.number().nullable(),
  status: zod.enum(['connected', 'disconnected']),
  lastMessageTime: zod.number().nullable(),
  messageCount: zod.number(),
  // For ResultOnJoint messages, recent message data to capture multiple errors
  recentJointResultsData: zod.array(zod.any()),
  // For all message types, the most recent message data
  lastMessageData: zod.any().optional(),
});

export const CANDiagnosticsUtilityData = zod.object({
  messages: zod.array(CANMessageStatus),
});

export const ControlBoxBoardStatus = zod.object({
  name: zod.enum(['Power Board', 'Safety Board', 'IO Board']),
  status: zod.enum(['connected', 'disconnected']),
  lastMessageTime: zod.number().nullable(),
  powerSupplyOutStatus: zod.enum(['active', 'inactive', 'unknown']).optional(),
  powerSupplyInStatus: zod.enum(['active', 'inactive', 'unknown']).optional(),
  vpathCurrent: zod.number().optional(),
});

export const ControlBoxDiagnosticsData = zod.object({
  boards: zod.array(ControlBoxBoardStatus),
});

export const SystemDiagnosticsData = zod.object({
  cpu: zod.object({
    cores: zod.number(),
    usagePercent: zod.number(),
    loadAvg: zod.array(zod.number()),
  }),
  memory: zod.object({
    totalMB: zod.number(),
    usedMB: zod.number(),
    freeMB: zod.number(),
    usagePercent: zod.number(),
  }),
});

export const ContainerResourceMetrics = zod.object({
  containerName: zod.string(),
  cpuUsagePercent: zod.number(),
  memoryUsageMB: zod.number(),
  memoryLimitMB: zod.number(),
  memoryUsagePercent: zod.number(),
  networkRxBytesPerSec: zod.number(),
  networkTxBytesPerSec: zod.number(),
});

export const ProcessGroupMetrics = zod.object({
  groupName: zod.string(),
  cpuUsagePercent: zod.number(),
  numThreads: zod.number(),
  contextSwitchesPerSec: zod.number(),
  memoryRssMB: zod.number(),
});

export const ThreadMetrics = zod.object({
  threadName: zod.string(),
  groupName: zod.string(),
  cpuUsagePercent: zod.number(),
  count: zod.number(),
});

export const NodejsMetrics = zod.object({
  eventLoopLagMeanSeconds: zod.number(),
  eluUtilization: zod.number(),
  heapUsedBytes: zod.number(),
  heapTotalBytes: zod.number(),
  gcDurationSecondsP50: zod.number(),
  gcDurationSecondsP99: zod.number(),
  activeHandlesTotal: zod.number(),
});

export const ContainerMetricsData = zod.object({
  containers: zod.array(ContainerResourceMetrics),
  processGroups: zod.array(ProcessGroupMetrics).optional(),
  threads: zod.array(ThreadMetrics).optional(),
  nodejs: NodejsMetrics.optional(),
  hostCpuUsagePercent: zod.number(),
  hostMemoryUsagePercent: zod.number(),
  hostMemoryTotalMB: zod.number(),
  timestamp: zod.string(),
});

export type EncoderErrors = zod.infer<typeof EncoderErrors>;
export type ServiceStatus = zod.infer<typeof ServiceStatus>;
export type DockerServicesDiagnosticsData = zod.infer<
  typeof DockerServicesDiagnosticsData
>;
export type JointStatus = zod.infer<typeof JointStatus>;
export type JointDiagnosticsData = zod.infer<typeof JointDiagnosticsData>;
export type CANMessageStatus = zod.infer<typeof CANMessageStatus>;
export type CANDiagnosticsUtilityData = zod.infer<
  typeof CANDiagnosticsUtilityData
>;
export type ControlBoxBoardStatus = zod.infer<typeof ControlBoxBoardStatus>;
export type ControlBoxDiagnosticsData = zod.infer<
  typeof ControlBoxDiagnosticsData
>;
export type SystemDiagnosticsData = zod.infer<typeof SystemDiagnosticsData>;
export type ContainerResourceMetrics = zod.infer<
  typeof ContainerResourceMetrics
>;
export type ProcessGroupMetrics = zod.infer<typeof ProcessGroupMetrics>;
export type ThreadMetrics = zod.infer<typeof ThreadMetrics>;
export type NodejsMetrics = zod.infer<typeof NodejsMetrics>;
export type ContainerMetricsData = zod.infer<typeof ContainerMetricsData>;
